import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DocumentItemEntity } from '../../../../meta-data/app-meta-data.service';
import {
  StructuredDocumentHierarchyModel,
  StructuredDocumentHierarchySectionModel,
  StructuredDocumentNavItemModel,
  StructuredDocumentSectionModel,
  StructuredDocumentViewerModel
} from '../../structured-content/models/structured-document-viewer.model';
import { StructuredDocumentViewerService } from '../../structured-content/services/structured-document-viewer.service';

interface StructuredDocumentNavGroupModel {
  subchapter: StructuredDocumentSectionModel;
  parts: StructuredDocumentSectionModel[];
}

interface StructuredDocumentPageOptionModel {
  documentSectionId: number;
  label: string;
}

interface StructuredDocumentAimNavGroupModel {
  chapter: StructuredDocumentSectionModel;
  sections: StructuredDocumentSectionModel[];
}

type StructuredViewerMode = 'generic' | 'far' | 'aim';

@Component({
  selector: 'app-document-reference-structured-viewer',
  templateUrl: './document-reference-structured-viewer.component.html',
  styleUrls: ['./document-reference-structured-viewer.component.scss']
})
export class DocumentReferenceStructuredViewerComponent implements OnChanges {
  private static readonly documentSectionTypeChapterId = 2;
  private static readonly documentSectionTypeSectionId = 6;
  private static readonly documentSectionTypeAppendixId = 7;
  private static readonly documentSectionTypeParagraphId = 8;

  @Input() documentItem: DocumentItemEntity;
  @Input() viewerData: StructuredDocumentViewerModel;
  @Input() hierarchyData: StructuredDocumentHierarchyModel | null = null;
  @Input() hideHeader: boolean = false;
  @Input() usePartChildDropdownNavigation: boolean = false;

  viewerMode: StructuredViewerMode = 'generic';
  searchText: string = '';

  navTree: StructuredDocumentNavItemModel[] = [];
  navGroups: StructuredDocumentNavGroupModel[] = [];
  aimNavGroups: StructuredDocumentAimNavGroupModel[] = [];
  leafSections: StructuredDocumentSectionModel[] = [];
  activeSection: StructuredDocumentSectionModel | null = null;
  activePart: StructuredDocumentSectionModel | null = null;
  activeChapter: StructuredDocumentSectionModel | null = null;
  activeAimSection: StructuredDocumentSectionModel | null = null;
  activeIndex: number = 0;
  isSectionLoading: boolean = false;
  sectionLoadError: string = '';
  selectedLeafSectionId: number | null = null;
  pageOptions: StructuredDocumentPageOptionModel[] = [];
  expandedChapterIds = new Set<number>();

  private sectionLookup = new Map<number, StructuredDocumentSectionModel>();
  private childLookup = new Map<number, StructuredDocumentSectionModel[]>();
  private hierarchyPartChildrenLookup = new Map<number, StructuredDocumentHierarchySectionModel[]>();
  private loadedSectionIds = new Set<number>();
  private loadingSectionIds = new Set<number>();

  constructor(private structuredDocumentViewerService: StructuredDocumentViewerService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.viewerData && !changes.hierarchyData && !changes.usePartChildDropdownNavigation) {
      return;
    }

    this.searchText = '';
    this.loadedSectionIds.clear();
    this.loadingSectionIds.clear();
    this.isSectionLoading = false;
    this.sectionLoadError = '';
    this.rebuildViewModel();
  }

  get filteredNavGroups(): StructuredDocumentNavGroupModel[] {
    const query = (this.searchText || '').trim().toLowerCase();
    if (!query) {
      return this.navGroups;
    }

    return this.navGroups
      .map(group => ({
        subchapter: group.subchapter,
        parts: group.parts.filter(part => this.matchesSearch(part, query))
      }))
      .filter(group => group.parts.length > 0 || this.matchesSearch(group.subchapter, query));
  }

  get currentSection(): StructuredDocumentSectionModel | null {
    return this.activeSection;
  }

  get currentSectionLabel(): string {
    return this.currentSection?.Label || this.currentSection?.SectionKey || '';
  }

  get currentSectionTitle(): string {
    return this.currentSection?.Title || this.viewerData?.PublicationName || this.documentItem?.DocumentItemName || '';
  }

  get currentPartTitle(): string {
    return this.activePart?.Title || this.viewerData?.PublicationName || this.documentItem?.DocumentItemName || '';
  }

  get currentSubchapterTitle(): string {
    const subchapter = this.activePart?.ParentDocumentSectionId
      ? this.sectionLookup.get(this.activePart.ParentDocumentSectionId)
      : null;

    return subchapter?.Title || '';
  }

  get currentAimChapterHeading(): string {
    return this.buildSectionHeading(this.activeChapter);
  }

  get currentAimSectionHeading(): string {
    return this.buildSectionHeading(this.activeAimSection);
  }

  get currentAimDetailHeading(): string {
    return this.buildSectionHeading(this.currentSection);
  }

  selectSection(section: StructuredDocumentSectionModel): void {
    if (this.viewerMode === 'far') {
      this.selectLeafSection(section);
      return;
    }

    if (this.viewerMode === 'aim') {
      this.selectAimSection(section);
      return;
    }

    const targetSection = this.resolveGenericTargetSection(section);
    this.activeSection = targetSection;
    this.activeIndex = Math.max(0, this.leafSections.findIndex(item => item.DocumentSectionId === targetSection.DocumentSectionId));
    this.sectionLoadError = '';
    this.ensureSectionContentLoaded(targetSection);
  }

  selectPart(part: StructuredDocumentSectionModel): void {
    if (!part || !part.DocumentSectionId) {
      return;
    }

    this.activePart = part;
    this.applyActivePartLeaves();
  }

  selectLeafSection(section: StructuredDocumentSectionModel): void {
    this.activeSection = section;
    this.activeIndex = Math.max(0, this.leafSections.findIndex(item => item.DocumentSectionId === section.DocumentSectionId));
    this.selectedLeafSectionId = section.DocumentSectionId || null;
    this.sectionLoadError = '';
    this.ensureSectionContentLoaded(section);
  }

  onPageDropdownChange(documentSectionIdValue: number | string): void {
    const documentSectionId = typeof documentSectionIdValue === 'number'
      ? documentSectionIdValue
      : parseInt(documentSectionIdValue, 10);
    if (isNaN(documentSectionId)) {
      return;
    }

    const section = this.leafSections.find(item => item.DocumentSectionId === documentSectionId);
    if (section) {
      this.selectLeafSection(section);
    }
  }

  selectAimSection(section: StructuredDocumentSectionModel): void {
    if (!section) {
      return;
    }

    this.activeAimSection = section;
    this.activeChapter = section.ParentDocumentSectionId
      ? this.sectionLookup.get(section.ParentDocumentSectionId) || this.activeChapter
      : this.activeChapter;

    if (this.activeChapter?.DocumentSectionId) {
      this.expandedChapterIds.add(this.activeChapter.DocumentSectionId);
    }

    this.applyActiveAimLeaves();
  }

  previousSection(): void {
    if (this.activeIndex > 0) {
      this.selectLeafSection(this.leafSections[this.activeIndex - 1]);
    }
  }

  nextSection(): void {
    if (this.activeIndex < this.leafSections.length - 1) {
      this.selectLeafSection(this.leafSections[this.activeIndex + 1]);
    }
  }

  isSectionActive(section: StructuredDocumentSectionModel): boolean {
    return this.activeSection?.DocumentSectionId === section.DocumentSectionId;
  }

  isPartActive(section: StructuredDocumentSectionModel): boolean {
    return this.activePart?.DocumentSectionId === section.DocumentSectionId;
  }

  trackByNav(index: number, item: StructuredDocumentNavItemModel): number | string {
    return item.section.DocumentSectionId || item.section.SectionKey || index;
  }

  trackByGroup(index: number, group: StructuredDocumentNavGroupModel): number | string {
    return group.subchapter.DocumentSectionId || group.subchapter.SectionKey || index;
  }

  trackByAimGroup(index: number, group: StructuredDocumentAimNavGroupModel): number | string {
    return group.chapter.DocumentSectionId || group.chapter.SectionKey || index;
  }

  trackBySection(index: number, section: StructuredDocumentSectionModel): number | string {
    return section.DocumentSectionId || section.SectionKey || index;
  }

  trackByAnchor(index: number, anchor: any): number | string {
    return anchor.DocumentAnchorId || anchor.AnchorKey || index;
  }

  getSectionBlocks(section: StructuredDocumentSectionModel | null | undefined): any[] {
    if (!section || !section.Content || !section.Content.length) {
      return [];
    }

    const blocks = section.Content[0]?.BlockJson || [];

    return this.viewerMode === 'far'
      ? this.stripDuplicateSectionHeading(blocks, section)
      : blocks;
  }

  getVisibleAnchors(section: StructuredDocumentSectionModel | null | undefined): any[] {
    if (!section?.Anchors?.length) {
      return [];
    }

    const sectionLabel = this.normalizeDisplayValue(section.Label || section.SectionKey);
    const seenLabels = new Set<string>();

    return section.Anchors.filter(anchor => {
      const anchorLabel = this.normalizeDisplayValue(anchor?.Label || anchor?.AnchorKey);
      if (!anchorLabel || anchorLabel === sectionLabel || seenLabels.has(anchorLabel)) {
        return false;
      }

      seenLabels.add(anchorLabel);
      return true;
    });
  }

  shouldShowGenericPaperEyebrow(section: StructuredDocumentSectionModel | null | undefined): boolean {
    if (!section) {
      return false;
    }

    const label = this.normalizeDisplayValue(section.Label || section.SectionKey);
    const title = this.normalizeDisplayValue(section.Title);

    return !!label && label !== title;
  }

  shouldShowGenericPaperTitle(section: StructuredDocumentSectionModel | null | undefined): boolean {
    if (!section) {
      return false;
    }

    if (this.hideHeader) {
      return true;
    }

    const title = this.normalizeDisplayValue(section.Title);
    const headerTitle = this.normalizeDisplayValue(this.currentSectionTitle);

    return !!title && title !== headerTitle;
  }

  isChapterExpanded(chapter: StructuredDocumentSectionModel): boolean {
    return !!chapter?.DocumentSectionId && this.expandedChapterIds.has(chapter.DocumentSectionId);
  }

  toggleChapter(chapter: StructuredDocumentSectionModel): void {
    const chapterId = chapter?.DocumentSectionId;
    if (!chapterId) {
      return;
    }

    this.activeChapter = chapter;

    if (this.expandedChapterIds.has(chapterId)) {
      this.expandedChapterIds.delete(chapterId);
      return;
    }

    this.expandedChapterIds.add(chapterId);
  }

  isAimSectionActive(section: StructuredDocumentSectionModel): boolean {
    return this.activeAimSection?.DocumentSectionId === section.DocumentSectionId;
  }

  private rebuildViewModel(): void {
    const sortedSections = (this.viewerData?.Sections || [])
      .slice()
      .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));

    this.sectionLookup = new Map<number, StructuredDocumentSectionModel>();
    this.childLookup = new Map<number, StructuredDocumentSectionModel[]>();
    this.navTree = [];
    this.navGroups = [];
    this.aimNavGroups = [];
    this.leafSections = [];
    this.activeSection = null;
    this.activePart = null;
    this.activeChapter = null;
    this.activeAimSection = null;
    this.activeIndex = 0;
    this.selectedLeafSectionId = null;
    this.pageOptions = [];
    this.hierarchyPartChildrenLookup = new Map<number, StructuredDocumentHierarchySectionModel[]>();
    this.expandedChapterIds = new Set<number>();

    sortedSections.forEach(section => {
      if (!section.DocumentSectionId) {
        return;
      }

      this.sectionLookup.set(section.DocumentSectionId, section);

      if (section.ParentDocumentSectionId) {
        const siblings = this.childLookup.get(section.ParentDocumentSectionId) || [];
        siblings.push(section);
        this.childLookup.set(section.ParentDocumentSectionId, siblings);
      }
    });

    const hasFarHierarchy = sortedSections.some(section => section.DocumentSectionTypeId === 3)
      && sortedSections.some(section => section.DocumentSectionTypeId === 4);
    const hasAimHierarchy = sortedSections.some(section => section.DocumentSectionTypeId === DocumentReferenceStructuredViewerComponent.documentSectionTypeChapterId);

    if (hasFarHierarchy) {
      this.buildHierarchyLookup();
      this.viewerMode = 'far';
      this.buildFarViewModel(sortedSections);
      return;
    }

    if (hasAimHierarchy) {
      this.viewerMode = 'aim';
      this.buildAimViewModel(sortedSections);
      return;
    }

    this.viewerMode = 'generic';
    this.buildGenericViewModel(sortedSections);
  }

  private buildAimViewModel(sortedSections: StructuredDocumentSectionModel[]): void {
    const chapters = sortedSections
      .filter(section => section.DocumentSectionTypeId === DocumentReferenceStructuredViewerComponent.documentSectionTypeChapterId);

    this.aimNavGroups = chapters
      .map(chapter => ({
        chapter,
        sections: (this.childLookup.get(chapter.DocumentSectionId || 0) || [])
          .filter(section => this.isAimNavSection(section))
          .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
      }))
      .filter(group => group.sections.length > 0);

    const firstGroup = this.aimNavGroups[0];
    if (!firstGroup) {
      return;
    }

    this.activeChapter = firstGroup.chapter;
    if (this.activeChapter.DocumentSectionId) {
      this.expandedChapterIds.add(this.activeChapter.DocumentSectionId);
    }

    this.activeAimSection = firstGroup.sections[0] || null;
    this.applyActiveAimLeaves();
  }

  private buildGenericViewModel(sortedSections: StructuredDocumentSectionModel[]): void {
    const navLookup = new Map<number, StructuredDocumentNavItemModel>();
    const rootItems: StructuredDocumentNavItemModel[] = [];

    sortedSections.forEach(section => {
      if (section.DocumentSectionId) {
        navLookup.set(section.DocumentSectionId, { section, children: [] });
      }
    });

    sortedSections.forEach(section => {
      const navItem = section.DocumentSectionId ? navLookup.get(section.DocumentSectionId) : null;

      if (!navItem) {
        return;
      }

      if (section.ParentDocumentSectionId && navLookup.has(section.ParentDocumentSectionId)) {
        navLookup.get(section.ParentDocumentSectionId)?.children.push(navItem);
      } else {
        rootItems.push(navItem);
      }
    });

    this.navTree = rootItems;
    this.leafSections = sortedSections.filter(section => this.isGenericLeafSection(section));
    this.activeSection = this.leafSections[0] || sortedSections[0] || null;
    this.activeIndex = this.activeSection
      ? Math.max(0, this.leafSections.findIndex(item => item.DocumentSectionId === this.activeSection?.DocumentSectionId))
      : 0;

    if (this.activeSection) {
      this.activeSection = this.resolveGenericTargetSection(this.activeSection);
      this.activeIndex = Math.max(0, this.leafSections.findIndex(item => item.DocumentSectionId === this.activeSection?.DocumentSectionId));
      this.ensureSectionContentLoaded(this.activeSection);
    }
  }

  private buildFarViewModel(sortedSections: StructuredDocumentSectionModel[]): void {
    const subchapters = sortedSections.filter(section => section.DocumentSectionTypeId === 3);
    this.navGroups = subchapters
      .map(subchapter => ({
        subchapter,
        parts: (this.childLookup.get(subchapter.DocumentSectionId || 0) || [])
          .filter(section => section.DocumentSectionTypeId === 4)
          .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
      }))
      .filter(group => group.parts.length > 0);

    const firstPart = this.navGroups.length ? this.navGroups[0].parts[0] : null;
    this.activePart = firstPart || null;
    this.applyActivePartLeaves();
  }

  private applyActivePartLeaves(): void {
    this.leafSections = this.resolveActivePartLeafSections();

    if (!this.leafSections.length) {
      this.activeSection = this.activePart;
      this.activeIndex = 0;
      this.selectedLeafSectionId = null;
      this.pageOptions = [];
      return;
    }

    if (!this.activeSection || !this.leafSections.some(section => section.DocumentSectionId === this.activeSection?.DocumentSectionId)) {
      this.activeSection = this.leafSections[0];
    }

    this.activeIndex = Math.max(0, this.leafSections.findIndex(item => item.DocumentSectionId === this.activeSection?.DocumentSectionId));
    this.selectedLeafSectionId = this.activeSection?.DocumentSectionId || null;
    this.pageOptions = this.leafSections
      .filter(section => !!section.DocumentSectionId)
      .map(section => ({
        documentSectionId: section.DocumentSectionId || 0,
        label: this.buildPageOptionLabel(section)
      }));

    if (this.activeSection) {
      this.ensureSectionContentLoaded(this.activeSection);
    }
  }

  private applyActiveAimLeaves(): void {
    const scopeSection = this.activeAimSection;
    if (!scopeSection) {
      this.leafSections = [];
      this.activeSection = null;
      this.activeIndex = 0;
      this.selectedLeafSectionId = null;
      this.pageOptions = [];
      return;
    }

    const resolvedLeaves = this.resolveAimLeafSections(scopeSection);
    this.leafSections = resolvedLeaves.length ? resolvedLeaves : [scopeSection];

    if (!this.activeSection || !this.leafSections.some(section => section.DocumentSectionId === this.activeSection?.DocumentSectionId)) {
      this.activeSection = this.leafSections[0];
    }

    this.activeIndex = Math.max(0, this.leafSections.findIndex(item => item.DocumentSectionId === this.activeSection?.DocumentSectionId));
    this.selectedLeafSectionId = this.activeSection?.DocumentSectionId || null;
    this.pageOptions = this.leafSections
      .filter(section => !!section.DocumentSectionId)
      .map(section => ({
        documentSectionId: section.DocumentSectionId || 0,
        label: this.buildPageOptionLabel(section)
      }));

    if (this.activeSection) {
      this.ensureSectionContentLoaded(this.activeSection);
    }
  }

  private resolveActivePartLeafSections(): StructuredDocumentSectionModel[] {
    if (!this.activePart?.DocumentSectionId) {
      return [];
    }

    if (this.usePartChildDropdownNavigation) {
      const hierarchyChildren = this.hierarchyPartChildrenLookup.get(this.activePart.DocumentSectionId) || [];
      const hierarchySections = hierarchyChildren
        .map(child => child.DocumentSectionId ? this.sectionLookup.get(child.DocumentSectionId) : null)
        .filter((section): section is StructuredDocumentSectionModel => !!section);

      if (hierarchySections.length) {
        return hierarchySections;
      }
    }

    return this.collectLeafSections(this.activePart);
  }

  private buildHierarchyLookup(): void {
    if (!this.usePartChildDropdownNavigation || !this.hierarchyData?.Groups?.length) {
      return;
    }

    this.hierarchyData.Groups.forEach(group => {
      (group.Parts || []).forEach(part => {
        const partSectionId = part.Section?.DocumentSectionId;
        if (!partSectionId) {
          return;
        }

        this.hierarchyPartChildrenLookup.set(partSectionId, (part.Children || []).slice());
      });
    });
  }

  private buildPageOptionLabel(section: StructuredDocumentSectionModel): string {
    const label = section.Label || section.SectionKey || 'Page';
    const title = section.Title || '';

    return title ? `${label} - ${title}` : label;
  }

  private normalizeDisplayValue(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
  }

  private buildSectionHeading(section: StructuredDocumentSectionModel | null | undefined): string {
    if (!section) {
      return '';
    }

    const label = (section.Label || section.SectionKey || '').trim();
    const title = (section.Title || '').trim();

    if (label && title) {
      return `${label}. ${title}`;
    }

    return label || title;
  }

  private collectLeafSections(part: StructuredDocumentSectionModel): StructuredDocumentSectionModel[] {
    const sections: StructuredDocumentSectionModel[] = [];
    const walk = (current: StructuredDocumentSectionModel) => {
      const currentId = current.DocumentSectionId;
      const children = currentId ? (this.childLookup.get(currentId) || []) : [];

      if (!children.length && (current.DocumentSectionTypeId === 6 || current.DocumentSectionTypeId === 7)) {
        sections.push(current);
        return;
      }

      children
        .slice()
        .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
        .forEach(child => walk(child));
    };

    walk(part);
    return sections;
  }

  private resolveAimLeafSections(section: StructuredDocumentSectionModel): StructuredDocumentSectionModel[] {
    const descendants = this.collectGenericLeafSections(section);
    return descendants.filter(item => item.DocumentSectionId !== section.DocumentSectionId);
  }

  private matchesSearch(section: StructuredDocumentSectionModel, query: string): boolean {
    return [
      section.Label,
      section.Title,
      section.SectionKey,
      section.SectionSlug
    ]
      .filter(value => !!value)
      .some(value => (value || '').toLowerCase().includes(query));
  }

  private isGenericLeafSection(section: StructuredDocumentSectionModel): boolean {
    const sectionId = section.DocumentSectionId;
    const children = sectionId ? (this.childLookup.get(sectionId) || []) : [];

    if (children.length) {
      return false;
    }

    return section.DocumentSectionTypeId === 6
      || section.DocumentSectionTypeId === 7
      || section.DocumentSectionTypeId === 8
      || !section.DocumentSectionTypeId;
  }

  private isAimNavSection(section: StructuredDocumentSectionModel): boolean {
    return section.DocumentSectionTypeId === DocumentReferenceStructuredViewerComponent.documentSectionTypeSectionId
      || section.DocumentSectionTypeId === DocumentReferenceStructuredViewerComponent.documentSectionTypeAppendixId;
  }

  private resolveGenericTargetSection(section: StructuredDocumentSectionModel): StructuredDocumentSectionModel {
    const descendants = this.collectGenericLeafSections(section);
    return descendants[0] || section;
  }

  private collectGenericLeafSections(section: StructuredDocumentSectionModel): StructuredDocumentSectionModel[] {
    const sections: StructuredDocumentSectionModel[] = [];
    const walk = (current: StructuredDocumentSectionModel) => {
      const currentId = current.DocumentSectionId;
      const children = currentId ? (this.childLookup.get(currentId) || []) : [];

      if (!children.length) {
        if (this.isGenericLeafSection(current)) {
          sections.push(current);
        }

        return;
      }

      children
        .slice()
        .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
        .forEach(child => walk(child));
    };

    walk(section);
    return sections;
  }

  private stripDuplicateSectionHeading(blocks: any[], section: StructuredDocumentSectionModel): any[] {
    if (!blocks.length || blocks[0]?.t !== 'h') {
      return blocks;
    }

    const headingText = this.normalizeHeadingText(this.inlineNodesToText(blocks[0].c));
    const sectionTitle = this.normalizeHeadingText(section.Title);
    const sectionHeading = this.normalizeHeadingText(`${section.Label || section.SectionKey || ''} ${section.Title || ''}`);

    if (headingText === sectionTitle || headingText === sectionHeading || (sectionTitle && headingText.endsWith(` ${sectionTitle}`))) {
      return blocks.slice(1);
    }

    return blocks;
  }

  private inlineNodesToText(nodes: any[]): string {
    return (Array.isArray(nodes) ? nodes : [])
      .map(node => this.inlineNodeToText(node))
      .join('');
  }

  private inlineNodeToText(node: any): string {
    if (!node) {
      return '';
    }

    if (typeof node === 'string') {
      return node;
    }

    if (node.t === 'text') {
      return node.s || '';
    }

    return this.inlineNodesToText(node.c);
  }

  private normalizeHeadingText(value: string | null | undefined): string {
    return (value || '')
      .replace(/^§\s*/, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  private ensureSectionContentLoaded(section: StructuredDocumentSectionModel | null): void {
    const documentItemId = this.documentItem?.DocumentItemId;
    const documentSectionId = section?.DocumentSectionId;
    const documentVersionId = this.viewerData?.DocumentVersionId;

    if (!documentItemId || !documentSectionId) {
      return;
    }

    if (this.loadedSectionIds.has(documentSectionId) || (section.Content && section.Content.length)) {
      this.loadedSectionIds.add(documentSectionId);
      return;
    }

    if (this.loadingSectionIds.has(documentSectionId)) {
      return;
    }

    this.loadingSectionIds.add(documentSectionId);
    this.isSectionLoading = true;
    this.sectionLoadError = '';

    this.structuredDocumentViewerService.getStructuredDocumentSection(documentItemId, documentSectionId, documentVersionId)
      .subscribe({
        next: loadedSection => {
          this.loadingSectionIds.delete(documentSectionId);
          this.loadedSectionIds.add(documentSectionId);
          this.mergeLoadedSection(loadedSection);
          this.isSectionLoading = false;
        },
        error: error => {
          this.loadingSectionIds.delete(documentSectionId);
          this.isSectionLoading = false;
          this.sectionLoadError = error?.message || 'Unable to load this section right now.';
        }
      });
  }

  private mergeLoadedSection(loadedSection: StructuredDocumentSectionModel): void {
    const documentSectionId = loadedSection?.DocumentSectionId;
    if (!documentSectionId) {
      return;
    }

    const targetSection = this.sectionLookup.get(documentSectionId);
    if (!targetSection) {
      return;
    }

    targetSection.Content = loadedSection.Content || [];
    targetSection.Anchors = loadedSection.Anchors || [];
    targetSection.Assets = loadedSection.Assets || [];
  }
}
